import React, { useEffect, useReducer } from 'react';
import { getBuildingSecurity, updateBuildingSecurity } from 'shared/api/security';
import { dateString } from 'shared/utils';
import { CardControls, CardSection } from 'shared/components/cardControls';
import { useRequest } from 'shared/hooks';
import {
  accessControlEntity,
  fireEntity,
  intrusionEntity,
  surveillanceEntity,
} from 'shared/entityDetails';
import { BoxWrapper, GridWrapper } from 'shared/components/data-display/GridFormatter';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { DateEditor, TextEditor } from 'shared/components/data-display/editors';
import { withAsyncSelect } from 'shared/components/data-display/withSelectors';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';

const accessControlDescriptions = [
  {
    key: 'name',
    formatter: (security) => security.access_control.name,
    label: 'Vendor',
    editor: withAsyncSelect(accessControlEntity),
    editKey: 'access_control_id',
  },
  {
    key: 'access_control_install_date',
    label: 'Install Date',
    formatter: (s) => dateString(s.access_control_install_date),
    editor: DateEditor,
  },
];

const fireDescriptions = [
  {
    key: 'fire_account',
    label: 'Account',
    editor: TextEditor,
  },
  {
    key: 'name',
    formatter: (security) => security.fire_monitor.name,
    label: 'Vendor',
    editor: withAsyncSelect(fireEntity),
    editKey: 'fire_monitor_id',
  },
  {
    key: 'fire_install_date',
    label: 'Install Date',
    formatter: (s) => dateString(s.fire_install_date),
    editor: DateEditor,
  },
];

const intrusionDescriptions = [
  {
    key: 'name',
    formatter: (security) => security.intrusion_monitor.name,
    label: 'Vendor',
    editor: withAsyncSelect(intrusionEntity),
    editKey: 'intrusion_monitor_id',
  },
  {
    key: 'intrusion_install_date',
    label: 'Install Date',
    formatter: (s) => dateString(s.intrusion_install_date),
    editor: DateEditor,
  },
];

const surveillanceDescriptions = [
  {
    key: 'name',
    formatter: (security) => security.surveillance.name,
    label: 'Vendor',
    editor: withAsyncSelect(surveillanceEntity),
    editKey: 'surveillance_id',
  },
  {
    key: 'surveillance_install_date',
    label: 'Install Date',
    formatter: (s) => dateString(s.surveillance_install_date),
    editor: DateEditor,
  },
];

/**
 * @param {Object<string, *>} buildingState - The building state object
 */
function Security({
  buildingState: {
    data: { id },
    editEnabled,
  },
}) {
  const {
    request,
    isLoading,
  } = useRequest();

  const [state, dispatch] = useReducer(dataReducer, initialDataReducerState);
  const {
    data: security,
    updatedData,
    dirty,
  } = state;

  const saveChanges = async () => {
    const updatedSecurity = await request(updateBuildingSecurity(id, updatedData), 'Updated security');
    if (updatedSecurity) {
      dispatch({
        action: DataReducerActions.set,
        payload: updatedSecurity,
      });
    }
  };

  useEffect(() => {
    async function fetchBuildingSecurity() {
      const securityResponse = await request(getBuildingSecurity(id));
      if (securityResponse) {
        dispatch({
          action: DataReducerActions.set,
          payload: securityResponse,
        });
      }
    }

    fetchBuildingSecurity();
  }, [id, request]);

  return (
    <CardSection saveChanges={saveChanges} editEnabled={editEnabled}>
      <CardControls
        clearChanges={() => dispatch({ action: DataReducerActions.clear })}
        disabled={isLoading}
        dirty={dirty}
        editEnabled={editEnabled}
      />
      <BoxWrapper>
        <GridWrapper
          size={{
            lg: 6,
            xs: 12,
          }}
          title="Access Control"
        >
          <DataFormatter
            descriptions={accessControlDescriptions}
            editing={editEnabled}
            data={security}
            updatedData={updatedData}
            updateValue={(key, value) => dispatch({
              action: DataReducerActions.update,
              payload: {
                key,
                value,
              },
            })}
          />
        </GridWrapper>
        <GridWrapper
          size={{
            lg: 6,
            xs: 12,
          }}
          title="Fire Monitor"
        >
          <DataFormatter
            descriptions={fireDescriptions}
            editing={editEnabled}
            data={security}
            updatedData={updatedData}
            updateValue={(key, value) => dispatch({
              action: DataReducerActions.update,
              payload: {
                key,
                value,
              },
            })}
          />
        </GridWrapper>
        <GridWrapper
          size={{
            lg: 6,
            xs: 12,
          }}
          title="Intrusion Detection"
        >
          <DataFormatter
            descriptions={intrusionDescriptions}
            editing={editEnabled}
            data={security}
            updatedData={updatedData}
            updateValue={(key, value) => dispatch({
              action: DataReducerActions.update,
              payload: {
                key,
                value,
              },
            })}
          />
        </GridWrapper>
        <GridWrapper
          size={{
            lg: 6,
            xs: 12,
          }}
          title="Video Surveillance"
        >
          <DataFormatter
            descriptions={surveillanceDescriptions}
            editing={editEnabled}
            data={security}
            updatedData={updatedData}
            updateValue={(key, value) => dispatch({
              action: DataReducerActions.update,
              payload: {
                key,
                value,
              },
            })}
          />
        </GridWrapper>
      </BoxWrapper>
    </CardSection>
  );
}

export default Security;
